import axios from '@/api/axios';

// ─── Types ──────────────────────────────────────────────────────

export interface FewShotExample {
  id: string;
  realm_id: string;
  source_feedback_id?: string;
  category: string;
  question: string;
  data_snapshot: string;
  ideal_response: string;
  prior_turns?: string;
  signal_type?: string;
  quality_score: number;
  is_active: boolean;
  needs_review: boolean;
  reviewed_at?: string;
  reviewed_by?: string;
  created_at: string;
  updated_at: string;
}

export interface FeedbackItem {
  id: string;
  realm_id: string;
  user_id: string;
  conversation_id: string;
  message_id: string;
  user_question: string;
  agent_response?: string;
  refined_response?: string;
  rating: 'up' | 'down';
  correction?: string;
  intent_category?: string;
  tool_id?: string;
  tool_success?: boolean;
  feedback_class?: 'refinement' | 'tool_failure' | 'unknown';
  curation_result?: 'promoted' | 'rejected' | null;
  curation_reason?: string;
  curated_at?: string;
  created_at: string;
}

export interface FeedbackStats {
  total: number;
  thumbsUp: number;
  thumbsDown: number;
  withCorrections: number;
  refinementCount: number;
  toolFailureCount: number;
  unknownClassCount: number;
  // Implicit signal stats
  implicitRephrase?: number;
  implicitAbandon?: number;
  implicitCorrection?: number;
  implicitFollowup?: number;
  totalImplicit?: number;
}

export interface PendingReviewsResponse {
  examples: FewShotExample[];
  count: number;
}

export interface FeedbackListResponse {
  items: FeedbackItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CategoryAnalytics {
  category: string;
  total: number;
  thumbsUp: number;
  thumbsDown: number;
  withCorrections: number;
  promotedCount: number;
}

export interface WeeklyTrend {
  week: string;
  total: number;
  thumbsUp: number;
  thumbsDown: number;
  approvalRate: number;
}

// ─── Stats & Overview ────────────────────────────────────────────

export async function getFeedbackStats(): Promise<FeedbackStats> {
  const res = await axios.get('/lex-agent/feedback/stats');
  return res.data;
}

export async function getPendingReviews(): Promise<PendingReviewsResponse> {
  const res = await axios.get('/lex-agent/feedback/reviews');
  return res.data;
}

export async function reviewExample(
  exampleId: string,
  action: 'approve' | 'reject',
): Promise<{ success: boolean; exampleId: string; action: string }> {
  const res = await axios.post(`/lex-agent/feedback/reviews/${exampleId}`, { action });
  return res.data;
}

// ─── Feedback Explorer ──────────────────────────────────────────

export async function getAllFeedback(params: {
  page?: number;
  limit?: number;
  rating?: 'up' | 'down';
  search?: string;
  category?: string;
  curationResult?: string;
  feedbackClass?: 'refinement' | 'tool_failure' | 'unknown';
  dateFrom?: string;
  dateTo?: string;
}): Promise<FeedbackListResponse> {
  const res = await axios.get('/lex-agent/feedback/all', { params });
  return res.data;
}

// ─── Analytics ──────────────────────────────────────────────────

export async function getCategoryAnalytics(): Promise<{ categories: CategoryAnalytics[] }> {
  const res = await axios.get('/lex-agent/feedback/analytics');
  return res.data;
}

export async function getWeeklyTrends(weeks = 12): Promise<{ trends: WeeklyTrend[] }> {
  const res = await axios.get('/lex-agent/feedback/trends', { params: { weeks } });
  return res.data;
}

// ─── Example Management ─────────────────────────────────────────

export async function getAllExamples(filters?: {
  category?: string;
  isActive?: boolean;
  needsReview?: boolean;
}): Promise<{ examples: FewShotExample[]; count: number }> {
  const res = await axios.get('/lex-agent/feedback/examples', { params: filters });
  return res.data;
}

export async function updateExample(
  exampleId: string,
  updates: { ideal_response?: string; question?: string; category?: string; data_snapshot?: string },
): Promise<{ success: boolean; example: FewShotExample }> {
  const res = await axios.put(`/lex-agent/feedback/examples/${exampleId}`, updates);
  return res.data;
}

export async function createManualExample(input: {
  category: string;
  question: string;
  idealResponse: string;
  dataSnapshot?: string;
}): Promise<{ success: boolean; example: FewShotExample }> {
  const res = await axios.post('/lex-agent/feedback/examples', input);
  return res.data;
}

// ─── Snapshot Regeneration ──────────────────────────────────

export async function regenerateSnapshot(input: {
  question: string;
  idealResponse: string;
  category: string;
}): Promise<{ dataSnapshot: string }> {
  const res = await axios.post('/lex-agent/feedback/examples/regenerate-snapshot', input);
  return res.data;
}

// ─── Original Response (Before/After) ───────────────────────

export async function getOriginalResponse(
  exampleId: string,
): Promise<{ originalResponse: string | null; userQuestion: string | null }> {
  const res = await axios.get(`/lex-agent/feedback/examples/${exampleId}/original`);
  return res.data;
}

// ─── AI Suggest Improvement ─────────────────────────────────

export async function suggestImprovement(input: {
  question: string;
  originalResponse: string;
  currentIdealResponse?: string;
  category: string;
}): Promise<{ suggestedResponse: string }> {
  const res = await axios.post('/lex-agent/feedback/examples/suggest-improvement', input);
  return res.data;
}

// ─── Bulk Actions ───────────────────────────────────────────────

export async function bulkReviewExamples(
  exampleIds: string[],
  action: 'approve' | 'reject',
): Promise<{ success: boolean; updated: number }> {
  const res = await axios.post('/lex-agent/feedback/reviews/bulk', { exampleIds, action });
  return res.data;
}

// ─── Export ─────────────────────────────────────────────────────

export function getExportUrl(): string {
  return '/lex-agent/feedback/export';
}

export async function exportExamplesJsonl(): Promise<Blob> {
  const res = await axios.get('/lex-agent/feedback/export', { responseType: 'blob' });
  return res.data;
}
